import isEqual from 'lodash/isEqual'

import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { ArtistId } from 'types/artist'
import type { ClientLibraryArtist, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'
import {
  clientLibraryArtistTracksLoad,
  clientLibraryArtistTracksUpdate,
} from '@disco/state-management/actions/client-library-artist-actions'
import {
  getClientLibraryArtist,
  getClientLibraryArtistTracks,
} from '@disco/state-management/selectors/client-libraries'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import { moveItems } from '@disco/utils/immutable'
import Row from 'components/common/row'
import TrackInfo from 'components/common/track-info'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Flex from 'components/ui/flex'
import Heading from 'components/ui/heading'
import { HoverProvider } from 'components/ui/hover-provider'
import InfiniteScroll from 'components/ui/infinite-scroll'

const getFeaturedTracksIds = (tracks: ClientLibraryArtist['tracks']) => {
  if (tracks?.list) {
    return tracks.list
      .map(id => tracks.items[id])
      .filter(track => track.is_featured)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(track => track.id)
  }

  return []
}

const LibrariesLibraryArtistsArtistFeaturedTracks = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams<{ libraryId: string; artistId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const artistId: ArtistId = parseInt(params.artistId!, 10)
  const artist: ClientLibraryArtist = useSelector((state: State) =>
    getClientLibraryArtist(state, libraryId, artistId),
  )
  const tracks = useSelector((state: State) =>
    getClientLibraryArtistTracks(state, libraryId, artistId),
  )
  const typeData = useComposerInsteadOfArtist()
  const [selectedTracks, setSelectedTracks] = useState([])
  useEffect(() => {
    dispatch(clientLibraryArtistTracksLoad(libraryId, artistId))
  }, [])
  useEffect(() => {
    if (artist.tracks) {
      setSelectedTracks(getFeaturedTracksIds(artist.tracks))
    }
  }, [artist.tracks])

  const toggleSelectedTrack = id => {
    if (selectedTracks.includes(id)) {
      setSelectedTracks(selectedTracks.filter(item => item !== id))
    } else {
      setSelectedTracks([...selectedTracks, id])
    }
  }

  const handleCancel = () => navigate(`/catalogs/${libraryId}/artists`)

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(
      clientLibraryArtistTracksUpdate(libraryId, artistId, selectedTracks),
    )
    navigate(`/catalogs/${libraryId}/artists`)
  }

  const loadNext = useCallback(
    (next: string) => {
      dispatch(clientLibraryArtistTracksLoad(libraryId, artistId, next))
    },
    [artistId, dispatch, libraryId],
  )

  const renderTracks = tracks => (
    <InfiniteScroll next={tracks.next} loadNext={loadNext}>
      {tracks.list.map((trackId, index) => {
        const track = tracks.items[trackId]
        const order = selectedTracks.findIndex(trackId => trackId === track.id)
        return (
          <Row key={index} columnsSize={[12]}>
            <div>
              <HoverProvider>
                <Flex spacing="large">
                  <Flex.Content>
                    <TrackInfo location="explorer" track={track} />
                  </Flex.Content>
                  {selectedTracks.includes(track.id) &&
                    selectedTracks.length > 1 && (
                      <CustomDropdown
                        className="dropdown-width--150"
                        options={selectedTracks.map((_, index) => ({
                          label: (index + 1).toString(),
                          value: index,
                        }))}
                        placeholder="Select order"
                        value={order}
                        onChange={option => {
                          setSelectedTracks(
                            moveItems(
                              selectedTracks,
                              [order],
                              option.value > order
                                ? option.value + 1
                                : option.value,
                            ),
                          )
                        }}
                      />
                    )}
                  <Checkbox
                    checked={selectedTracks.includes(track.id)}
                    onChange={() => toggleSelectedTrack(track.id)}
                  />
                </Flex>
              </HoverProvider>
            </div>
          </Row>
        )
      })}
    </InfiniteScroll>
  )

  return (
    <>
      <Heading size={500}>Featured tracks</Heading>
      <Row columnsSize={[12]}>
        <div>
          <div className="form-note">
            Featured tracks appear at the top of your {typeData.value}’s Catalog
            page.
          </div>
        </div>
      </Row>
      <Row columnsSize={[12]}>
        <div>
          {artist.loading ||
          !tracks ||
          (tracks.loading && tracks.list.length === 0)
            ? 'Loading...'
            : renderTracks(tracks)}
        </div>
      </Row>
      <Row columnsSize={[12]}>
        <div>
          <ButtonContainer>
            <Button
              appearance="primary"
              caption="Save"
              disabled={
                artist.loading ||
                isEqual(getFeaturedTracksIds(artist.tracks), selectedTracks)
              }
              onClick={handleSubmit}
            />
            <Button appearance="link" caption="Cancel" onClick={handleCancel} />
          </ButtonContainer>
        </div>
      </Row>
    </>
  )
}

export default LibrariesLibraryArtistsArtistFeaturedTracks
